import axios from 'axios';
import type { PaginatedApiResponse } from 'types/api.types';
import type {
  EventDetails,
  EventDetailsResponse,
  EventsListResponse,
  EventsQuery,
  EventSummary,
} from './events.types';

interface AuthenticatedRequestOptions {
  idToken: string;
  signal?: AbortSignal;
}

type LoadEventsOptions = EventsQuery & AuthenticatedRequestOptions;

interface LoadEventDetailsOptions extends AuthenticatedRequestOptions {
  eventId: string;
}

const getApiBaseUrl = (): string => {
  const baseUrl = process.env.REACT_APP_AMA_V2_API_URL?.trim();
  if (!baseUrl) {
    throw new Error('Events API URL is not configured');
  }

  return baseUrl.replace(/\/+$/, '');
};

const getAuthorizationHeader = (idToken: string): string => {
  const normalizedToken = idToken.trim();
  if (!normalizedToken) {
    throw new Error('Authentication token is not available');
  }

  // Existing AMA APIs expect the Cognito ID token without a Bearer prefix.
  return normalizedToken;
};

const throwResponseError = (error: string | null): void => {
  if (error) {
    throw new Error(error);
  }
};

export const getEvents = async (
  options: LoadEventsOptions,
): Promise<PaginatedApiResponse<EventSummary>> => {
  const {
    clientName,
    eventName,
    idToken,
    initiatedBy,
    operationStatus,
    page,
    pageSize,
    search,
    signal,
    sortDirection,
    sortField,
  } = options;

  const response = await axios.get<EventsListResponse>(`${getApiBaseUrl()}/events`, {
    headers: {
      Accept: 'application/json',
      Authorization: getAuthorizationHeader(idToken),
    },
    params: {
      page,
      page_size: pageSize,
      search,
      event_name: eventName,
      client_name: clientName,
      initiated_by: initiatedBy,
      operation_status: operationStatus,
      sort_by: sortField,
      sort_direction: sortDirection,
    },
    signal,
  });

  throwResponseError(response.data.error);

  return {
    error: null,
    items: Array.isArray(response.data.items) ? response.data.items : [],
    message: response.data.message,
    page: response.data.page,
    totalItems: response.data.total_count,
    totalPages: response.data.total_pages,
  };
};

export const getEventDetails = async (
  options: LoadEventDetailsOptions,
): Promise<EventDetails> => {
  const { eventId, idToken, signal } = options;
  const normalizedEventId = eventId.trim();

  if (!normalizedEventId) {
    throw new Error('Event ID is required');
  }

  const response = await axios.get<EventDetailsResponse>(`${getApiBaseUrl()}/events/detail`, {
    headers: {
      Accept: 'application/json',
      Authorization: getAuthorizationHeader(idToken),
    },
    params: { event_id: normalizedEventId },
    signal,
  });

  throwResponseError(response.data.error);

  if (!response.data.item) {
    throw new Error('Event details were not returned by the server');
  }

  return response.data.item;
};
